import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExcelDownloadReport from '../../components/common/excel/download';
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  Nav,
  Row,
  Tab,
} from '@themesberg/react-bootstrap';
import BreadCrumbComponent from '../../components/common/Breadcrumb';
import { getReplacementReport } from '../../parse-functions/reports';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateLeft, faFileExport } from '@fortawesome/free-solid-svg-icons';
import EditableDropDown from '../../components/common/EditableDropdown';
import { returnWarehouseDropdownOptions } from '../../utils/warehouse';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import { useDispatch, useSelector } from 'react-redux';
import { updateToastInfo } from '../../actions/settings';
import { updateSpinnerState } from '../../actions/spinner';
import ReplacementChart from './replacementChart';
import { replacementReportColumns, replacementReportExport } from './utils';
import TableOnly from '../scheme-management/Components/IndividualScheme/TableOnly';

const ReplacementReport = () => {
  const { t } = useTranslation();
  const configData = useSelector((state) => state?.user?.config);
  const dispatch = useDispatch();

  const breadCrumbItems = [
    {
      title: t('Replacement Report'),
      link: '',
    },
  ];

  const initialFormInput = {
    startDate: '',
    endDate: '',
    branch: '',
    zone: '',
  };
  const [reportData, setReportData] = useState({});
  const [formInput, setFormInput] = useState(initialFormInput);
  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );

  const handleFilterSubmit = async (startDate, endDate) => {
    try {
      dispatch(updateSpinnerState(true));
      const input = {
        ...formInput,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        branch: formInput.branch,
        zone: formInput.zone,
      };
      const report = await getReplacementReport(input);
      setFormInput(input);
      setReportData(report);
      console.log(report);
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          message: 'Replacement Report fetched successfully',
        })
      );
    } catch (error) {
      console.log(error);
      dispatch(
        updateToastInfo({
          show: true,
          type: 'error',
          message: error.message,
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <BreadCrumbComponent items={breadCrumbItems} />
          <h4>{t('Replacement Report')}</h4>
          <p className="mb-0">
            {'Report is only available from 1st June 2025'}
          </p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup>
            <Button
              variant={'white'}
              onClick={() => {
                replacementReportExport(reportData?.data);
              }}
              disabled={reportData?.data?.length === 0}
              size="md"
            >
              <FontAwesomeIcon icon={faFileExport} />
              &nbsp;&nbsp;{t('Export')}
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <>
        <div
          className="table-settings mt-4 mb-4"
          style={{
            background: 'white',
            transition: 'max-height 0.7s ease-in-out',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
              width: '100%',
              backgroundColor: 'white',
            }}
          >
            <Form.Group>
              <Form.Label>Branch Name</Form.Label>
              <EditableDropDown
                id={'warehouses'}
                onChange={(e) =>
                  setFormInput((prev) => ({
                    ...prev,
                    branch: e.value,
                  }))
                }
                backgroundColor="#eaedf2"
                value={formInput['branch']}
                options={returnWarehouseDropdownOptions(otherWarehouses)}
                placeholder={'Select branch'}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Zone</Form.Label>
              <EditableDropDown
                onChange={(e) =>
                  setFormInput((prev) => ({
                    ...prev,
                    zone: e.value,
                  }))
                }
                options={arrayToDropdownOptions(Object.keys(configData?.zones))}
                placeholder={'Select an Option'}
                value={formInput['zone']}
              />
            </Form.Group>

            <Form.Group className="d-flex align-items-end">
              <DateRangeSelector
                filtersValue={formInput}
                onSubmit={handleFilterSubmit}
              />
            </Form.Group>
          </div>
          <div
            style={{
              gridColumn: 'span 3',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            <Button
              variant="white"
              onClick={() => setFormInput(initialFormInput)}
              style={{
                width: '150px',
                height: '38px',
                borderRadius: 0,
                background: '#EAEDF2',
                marginTop: '10px',
              }}
            >
              <FontAwesomeIcon icon={faArrowRotateLeft} />
              &nbsp;&nbsp;{t('Reset')}
            </Button>
          </div>
        </div>
      </>

      <Tab.Container defaultActiveKey="report">
        <Row>
          <Col lg={12}>
            <Nav className="nav-tabs">
              <Nav.Item>
                <Nav.Link eventKey="report" className="mb-sm-3 mb-md-0">
                  {t('Report')}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="graph" className="mb-sm-3 mb-md-0">
                  {t('Graph')}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          <Col lg={12}>
            <Tab.Content>
              <Tab.Pane eventKey="report" className="py-4">
                <TableOnly
                  id={'replacementReport'}
                  data={reportData?.data || []}
                  columns={replacementReportColumns()}
                />
              </Tab.Pane>
              {reportData?.chartData && (
                <Tab.Pane eventKey="graph" className="py-4">
                  <ReplacementChart
                    chartData={reportData?.chartData}
                    type={reportData.type}
                  />
                </Tab.Pane>
              )}
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </div>
  );
};

export default ReplacementReport;
